import { alpha, createTheme, type PaletteMode } from "@mui/material";
import {
  borderRadius,
  elevation,
  motion,
  spacing,
  brandColors,
  neutral,
  getThemeMode,
  createGradients,
} from "@/theme/tokens";
import { accessibleForegroundOn } from "@/theme/accessible-on-fill";
import { THEME_VARIANT, type ThemeVariant } from "@/constants/theme-variant";
import { resolveThemePreference } from "@/constants/theme-preference";
import { getTypographyVariants, fontFamily } from "@/theme/typography";

/**
 * Selection semantic tokens.
 *
 * "Selection" = the user-picked state (radio cards, chips, tabs, payment row, offer row,
 * address card, modifier option, …). It is intentionally distinct from `success` (which
 * is reserved for applied / completed / confirmed feedback such as "offer applied",
 * "you saved ₹X", "order delivered"). Both axes are computable from the existing palette
 * so theme/dark-mode behaviour is automatic and components never compute these locally.
 */
export interface SelectionPaletteTokens {
  /** 4% primary tint — pre-selection hover preview on selectable surfaces. */
  hoverSurface: string;
  /** ~6% (light) / ~10% (dark) primary tint — expandable panels, summary chrome. */
  surfaceFaint: string;
  /** 8% (light) / 12% (dark) primary tint — chips, light radio rows, "current" combo option. */
  surfaceSubtle: string;
  /** 14% (light) / 20% (dark) primary tint — radio cards, payment row, modifier option. */
  surfaceTinted: string;
  /** Solid primary fill — solid filter chip, active meal-type tab. */
  surfaceSolid: string;
  /** ~8% (light) / ~14% (dark) primary border — soft panel outlines. */
  borderSoft: string;
  /** 40% primary border — mid-strength selected-card outline. */
  border: string;
  /** Solid primary border — strong selected-card outline (e.g. payment row, offer row). */
  borderStrong: string;
  /** Primary main — text/icons on subtle/tinted selection surfaces. */
  text: string;
  /** Primary contrast text — text/icons on solid selection fill. */
  textOnSolid: string;
  /** Primary main — focus-visible outline colour for selectable surfaces. */
  ring: string;
  /** Softer primary outline — focused text fields (matches legacy ~50% primary border). */
  focusOutline: string;
}

/**
 * Active-navigation tokens.
 * Anchored to `secondary.main` so the existing dark-toned bottom-nav style is preserved
 * while moving the values out of components.
 */
export interface NavActivePaletteTokens {
  surface: string;
  border: string;
  text: string;
}

declare module "@mui/material/styles" {
  interface Palette {
    custom: {
      surface: string;
      surfaceHover: string;
      /**
       * Lightweight surface tint — separator-driven list rows, hovered list items,
       * subtle grouping chrome. Use this instead of `alpha(text.primary, …)` math
       * inside components.
       */
      surfaceSoft: string;
      /**
       * Soft separator color — visible but low-contrast horizontal/vertical dividers
       * inside flowing list layouts. Use this instead of `theme.palette.divider`
       * (which is intentionally transparent in this app).
       */
      separatorSubtle: string;
      accent: string;
      accentHover: string;
      onAccent: string;
      border: string;
      textPrimary: string;
      selection: SelectionPaletteTokens;
      navActive: NavActivePaletteTokens;
    };
  }
  interface PaletteOptions {
    custom?: {
      surface: string;
      surfaceHover: string;
      surfaceSoft: string;
      separatorSubtle: string;
      accent: string;
      accentHover: string;
      onAccent: string;
      border: string;
      textPrimary: string;
      selection: SelectionPaletteTokens;
      navActive: NavActivePaletteTokens;
    };
  }
  interface Theme {
    customTokens: {
      themeVariant: ThemeVariant;
      cta: {
        fill: string;
        fillHover: string;
        fillActive: string;
        text: string;
        gradientStart: string;
        gradientEnd: string;
        shadow: string;
      };
      gradients: ReturnType<typeof createGradients>;
    };
  }
  interface ThemeOptions {
    customTokens?: {
      themeVariant?: ThemeVariant;
      cta?: {
        fill?: string;
        fillHover?: string;
        fillActive?: string;
        text?: string;
        gradientStart?: string;
        gradientEnd?: string;
        shadow?: string;
      };
      gradients?: ReturnType<typeof createGradients>;
    };
  }
}

/**
 * designTokens — backward-compatible shape for existing component imports.
 * Color entries default to Classic Amber; use MUI theme for active variant + mode.
 */
const amberLight = getThemeMode(THEME_VARIANT.AMBER, "light");
const amberDark = getThemeMode(THEME_VARIANT.AMBER, "dark");
const legacyGradients = {
  ...createGradients(amberLight),
  primaryDark: createGradients(amberDark).primary,
} as const;

export const designTokens = {
  light: {
    ...amberLight,
    background: amberLight.background,
    card: amberLight.card,
    primary: amberLight.primary,
    mutedText: amberLight.mutedText,
    border: amberLight.border,
    accent: amberLight.accent,
    cta: amberLight.cta,
  },
  dark: {
    ...amberDark,
    background: amberDark.background,
    card: amberDark.card,
    primary: amberDark.primary,
    mutedText: amberDark.mutedText,
    border: amberDark.border,
    accent: amberDark.accent,
    cta: amberDark.cta,
  },
  typography: {
    fontFamily,
  },
  shape: {
    borderRadius,
  },
  spacing,
  motion,
  gradients: legacyGradients,
  shadows: elevation,
  success: brandColors.success,
  warning: brandColors.warning,
  error: brandColors.error,
} as const;

export function createAppTheme(
  mode: PaletteMode | string = "light",
  themeVariant: ThemeVariant | string = THEME_VARIANT.AMBER,
) {
  const safe = resolveThemePreference({ themeStyle: themeVariant, themeMode: mode });
  const tokens = getThemeMode(safe.themeStyle, safe.themeMode);
  const gradientTokens = createGradients(tokens);
  const isDark = safe.themeMode === "dark";
  const resolvedMode = safe.themeMode;
  const resolvedVariant = safe.themeStyle;

  // Anchors for semantic tokens. `accentBase` (= primary.main) drives "selection";
  // `navBase` (= secondary.main) drives "active navigation". All alpha math lives
  // here so components never derive selection colours locally.
  const accentBase = tokens.accent;
  const navBase = tokens.primary;

  const selection: SelectionPaletteTokens = {
    hoverSurface: alpha(accentBase, 0.04),
    surfaceFaint: alpha(accentBase, isDark ? 0.1 : 0.06),
    surfaceSubtle: alpha(accentBase, isDark ? 0.12 : 0.08),
    surfaceTinted: alpha(accentBase, isDark ? 0.2 : 0.14),
    surfaceSolid: accentBase,
    borderSoft: alpha(accentBase, isDark ? 0.14 : 0.08),
    border: alpha(accentBase, isDark ? 0.5 : 0.4),
    borderStrong: accentBase,
    text: accentBase,
    textOnSolid: accessibleForegroundOn(accentBase),
    ring: accentBase,
    focusOutline: alpha(accentBase, 0.5),
  };

  const navActive: NavActivePaletteTokens = {
    surface: alpha(navBase, isDark ? 0.2 : 0.14),
    border: alpha(navBase, isDark ? 0.28 : 0.2),
    text: navBase,
  };

  return createTheme({
    customTokens: {
      themeVariant: resolvedVariant,
      cta: tokens.cta,
      gradients: gradientTokens,
    },
    palette: {
      mode: resolvedMode,
      primary: { main: tokens.accent, contrastText: neutral.white },
      secondary: { main: tokens.primary, contrastText: neutral.white },
      success: { main: brandColors.success },
      warning: { main: brandColors.warning },
      error: { main: brandColors.error },
      background: {
        default: tokens.background,
        paper: tokens.card,
      },
      text: {
        primary: tokens.primary,
        secondary: tokens.mutedText,
      },
      divider: tokens.border,
      custom: {
        surface: tokens.card,
        surfaceHover: alpha(tokens.card, 0.85),
        // Separator-driven list surfaces. These read off `text.primary` (mode-aware)
        // so dark/light cohesion is automatic.
        surfaceSoft: alpha(tokens.primary, isDark ? 0.06 : 0.04),
        separatorSubtle: alpha(tokens.primary, isDark ? 0.1 : 0.08),
        accent: tokens.accent,
        accentHover: isDark ? alpha(tokens.accent, 0.9) : tokens.accent,
        onAccent: neutral.white,
        border: tokens.border,
        textPrimary: tokens.primary,
        selection,
        navActive,
      },
    },
    typography: getTypographyVariants(),
    shape: {
      borderRadius: borderRadius.md,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: `${borderRadius.lg}px`,
            transition: `transform ${motion.fast}`,
            "&:active": {
              transform: "scale(0.98)",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.custom.selection.ring}`,
              outlineOffset: 2,
            },
          }),
          sizeLarge: {
            height: "56px",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: `transform ${motion.fast}`,
            boxShadow: elevation.chip,
            cursor: "pointer",
            "@media (hover: hover)": {
              "&:hover": {
                transform: "scale(1.02)",
              },
            },
            "&:active": {
              transform: "scale(0.98)",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.custom.selection.ring}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            transition: `transform ${motion.fast}`,
            cursor: "pointer",
            "&:active": {
              transform: "scale(0.98)",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.custom.selection.ring}`,
              outlineOffset: 2,
            },
          }),
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: `${borderRadius.lg}px !important`,
            boxShadow: elevation.hover,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            padding: "6px 14px",
            borderRadius: `${borderRadius.sm}px`,
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            borderRadius: "50vh",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: borderRadius.md,
            boxShadow: elevation.card,
            backgroundColor:
              theme.palette.mode === "light"
                ? neutral.white
                : theme.palette.background.paper,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: borderRadius.lg,
            boxShadow: elevation.card,
            backgroundColor:
              theme.palette.mode === "light"
                ? neutral.white
                : theme.palette.background.paper,
          }),
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: alpha(theme.palette.text.primary, 0.06),
          }),
        },
      },
    },
  });
}
